import React, { useState } from 'react';

interface PathogensTabProps {
  reportData?: any;
}

const PathogensTab: React.FC<PathogensTabProps> = ({ reportData }) => {
  const [expandedOrganism, setExpandedOrganism] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string>('bacteria');

  // Default data in case no report data is provided
  const bacteria = reportData?.analysis_results?.pathogens?.detected?.bacteria || [
    { name: "Escherichia coli", result: "Detected" }
  ];
  
  const viruses = reportData?.analysis_results?.pathogens?.detected?.viruses || [];
  const fungi = reportData?.analysis_results?.pathogens?.detected?.fungi || [];
  const parasites = reportData?.analysis_results?.pathogens?.detected?.parasites || [];
  
  const weightedPathogens = reportData?.analysis_results?.weighted_pathogens || [
    {
      name: "Escherichia coli",
      standardized_name: "escherichia coli",
      category: "bacteria",
      weight: 36.12,
      priority_rank: 1
    }
  ];

  const toggleOrganism = (name: string) => {
    if (expandedOrganism === name) {
      setExpandedOrganism(null);
    } else {
      setExpandedOrganism(name);
    }
  };

  const toggleCategory = (category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory('');
    } else {
      setExpandedCategory(category);
    }
  };

  const handleExpandFirst = () => {
    if (bacteria.length > 0) {
      setExpandedOrganism(bacteria[0].name);
    }
  };

  const handleCollapseAll = () => {
    setExpandedOrganism(null);
  };

  const getClinicalText = (organismName: string) => {
    if (organismName === "Escherichia coli") {
      return "While primarily known for GI and UTI infections, E. coli can cause pneumonia in hospitalized patients, particularly in those who aspirate. Common symptoms include: Fever, Productive cough, Purulent sputum, Dyspnea, Pleuritic chest pain. Risk factors include: Hospitalization, Aspiration risk, Immunocompromised status, Advanced age, Recent antibiotic use.";
    }
    return "This organism is a pathogen that may cause various types of infections depending on the site and host factors.";
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="border border-border rounded-md mb-6">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Detected Organisms Summary</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Total Detected Organisms</h4>
                <p className="mb-2"><span className="font-medium">Total:</span> {
                  bacteria.length + viruses.length + fungi.length + parasites.length
                }</p>
                <p className="mb-1"><span className="font-medium">Bacteria:</span> {bacteria.length}</p>
                <p className="mb-1"><span className="font-medium">Viruses:</span> {viruses.length}</p>
                <p className="mb-1"><span className="font-medium">Fungi:</span> {fungi.length}</p>
                <p className="mb-1"><span className="font-medium">Parasites:</span> {parasites.length}</p>
              </div>
              
              <div>
                <h4 className="font-medium mb-3">Organism Weights</h4>
                <div className="space-y-3">
                  {weightedPathogens.map((pathogen: any, index: number) => (
                    <div key={index} className="bg-background-alt p-3 rounded">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">{pathogen.name}</span>
                        <span>{pathogen.weight?.toFixed(2)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-primary h-2.5 rounded-full" 
                          style={{ width: `${pathogen.weight}%` }}
                        ></div>
                      </div>
                      <div className="text-xs mt-1">
                        Priority Rank: {pathogen.priority_rank}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-border rounded-md mb-6">
          <div className="p-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-text">Detected Organisms</h3>
            <div className="space-x-2">
              <button 
                onClick={handleExpandFirst}
                className="text-primary px-4 py-1 border border-primary rounded text-sm flex items-center"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Expand First
              </button>
              <button 
                onClick={handleCollapseAll}
                className="text-primary px-4 py-1 border border-primary rounded text-sm flex items-center"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Collapse All
              </button>
            </div>
          </div>
          
          <div className="border-t border-border">
            {/* Bacteria Section */}
            <div className="border-b border-border">
              <div 
                className={`p-4 bg-gray-50 flex justify-between items-center cursor-pointer`}
                onClick={() => toggleCategory('bacteria')}
              >
                <div className="flex items-center">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-red-100 text-red-800 mr-3">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </span>
                  <span className="font-medium">Bacteria</span>
                  <span className="ml-3 text-xs px-2 py-0.5 bg-gray-200 rounded-full">
                    {bacteria.length} organism
                  </span>
                </div>
                <svg 
                  className={`h-5 w-5 transform ${expandedCategory === 'bacteria' ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {expandedCategory === 'bacteria' && bacteria.map((organism: any, index: number) => (
                <div key={index}>
                  <div 
                    className="border-t border-gray-200 p-4 pl-12 flex justify-between items-center cursor-pointer"
                    onClick={() => toggleOrganism(organism.name)}
                  >
                    <div className="flex items-center">
                      <span className="font-medium">{organism.name}</span>
                      <span className="ml-3 text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                        {organism.result}
                      </span>
                    </div>
                    <svg 
                      className={`h-5 w-5 transform ${expandedOrganism === organism.name ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  
                  {expandedOrganism === organism.name && (
                    <div className="border-t border-gray-200 bg-white p-6 pl-12">
                      <p className="mb-4">{organism.name} is a pathogen that may cause various types of infections depending on the site and host factors.</p>
                      
                      <h4 className="font-medium text-lg mb-2">Clinical Effects</h4>
                      <p className="mb-4">{getClinicalText(organism.name)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Viruses Section */}
            {viruses.length > 0 && (
              <div className="border-b border-border">
                <div 
                  className={`p-4 bg-gray-50 flex justify-between items-center cursor-pointer`}
                  onClick={() => toggleCategory('viruses')}
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-800 mr-3">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </span>
                    <span className="font-medium">Viruses</span>
                    <span className="ml-3 text-xs px-2 py-0.5 bg-gray-200 rounded-full">
                      {viruses.length} organism
                    </span>
                  </div>
                  <svg 
                    className={`h-5 w-5 transform ${expandedCategory === 'viruses' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {expandedCategory === 'viruses' && viruses.map((organism: any, index: number) => (
                  <div key={index}>
                    <div 
                      className="border-t border-gray-200 p-4 pl-12 flex justify-between items-center cursor-pointer"
                      onClick={() => toggleOrganism(organism.name)}
                    >
                      <div className="flex items-center">
                        <span className="font-medium">{organism.name}</span>
                        <span className="ml-3 text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                          {organism.result}
                        </span>
                      </div>
                      <svg 
                        className={`h-5 w-5 transform ${expandedOrganism === organism.name ? 'rotate-180' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {expandedOrganism === organism.name && (
                      <div className="border-t border-gray-200 bg-white p-6 pl-12">
                        <p className="mb-4">{organism.name} is a viral pathogen that may cause various types of infections.</p>
                        
                        <h4 className="font-medium text-lg mb-2">Clinical Effects</h4>
                        <p className="mb-4">Clinical presentation varies based on the specific virus and patient factors.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Fungi Section */}
            {fungi.length > 0 && (
              <div className="border-b border-border">
                <div 
                  className={`p-4 bg-gray-50 flex justify-between items-center cursor-pointer`}
                  onClick={() => toggleCategory('fungi')}
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-100 text-yellow-800 mr-3">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </span>
                    <span className="font-medium">Fungi</span>
                    <span className="ml-3 text-xs px-2 py-0.5 bg-gray-200 rounded-full">
                      {fungi.length} organism
                    </span>
                  </div>
                  <svg 
                    className={`h-5 w-5 transform ${expandedCategory === 'fungi' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {expandedCategory === 'fungi' && fungi.map((organism: any, index: number) => (
                  <div key={index} className="border-t border-gray-200">
                    {/* Organism details would go here */}
                  </div>
                ))}
              </div>
            )}
            
            {/* Parasites Section */}
            {parasites.length > 0 && (
              <div>
                <div 
                  className={`p-4 bg-gray-50 flex justify-between items-center cursor-pointer`}
                  onClick={() => toggleCategory('parasites')}
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-800 mr-3">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </span>
                    <span className="font-medium">Parasites</span>
                    <span className="ml-3 text-xs px-2 py-0.5 bg-gray-200 rounded-full">
                      {parasites.length} organism
                    </span>
                  </div>
                  <svg 
                    className={`h-5 w-5 transform ${expandedCategory === 'parasites' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {expandedCategory === 'parasites' && parasites.map((organism: any, index: number) => (
                  <div key={index} className="border-t border-gray-200">
                    {/* Organism details would go here */}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathogensTab; 